import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { enablementOverviewService } from '../../../services/enablementOutcomesService';
import { useToast } from '../../../contexts/ToastContext';

const StatCard = ({ icon, label, value, sub, color, trend = null }) => (
  <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm p-14 flex items-center gap-12 hover:shadow-md transition-shadow">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon name={icon} size={18} className="text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider leading-tight">{label}</p>
      <p className="text-2xl font-bold text-slate-900 leading-tight mt-1">{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-1 leading-tight">{sub}</p>}
      {trend && (
        <div className={`flex items-center gap-3 mt-1 text-[10px] font-semibold ${trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          <Icon name={trend > 0 ? 'TrendingUp' : 'TrendingDown'} size={10} />
          {Math.abs(trend)}% vs last quarter
        </div>
      )}
    </div>
  </div>
);

const TierBadge = ({ tier }) => {
  const map = {
    platinum: 'bg-violet-100 text-violet-700',
    gold: 'bg-amber-100 text-amber-700',
    silver: 'bg-slate-100 text-slate-600',
    bronze: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`px-8 py-3 rounded-lg text-xs font-bold uppercase tracking-wide ${map?.[tier] || 'bg-slate-100 text-slate-600'}`}>
      {tier || 'N/A'}
    </span>
  );
};

const ScorePill = ({ score }) => {
  const color = score >= 80 ? 'text-emerald-700 bg-emerald-50' : score >= 65 ? 'text-amber-700 bg-amber-50' : 'text-red-700 bg-red-50';
  return <span className={`px-10 py-4 rounded-lg text-sm font-bold ${color}`}>{score || '—'}</span>;
};

const ProgressBar = ({ pct, color = 'bg-emerald-500' }) => (
  <div className="flex items-center gap-10">
    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
    </div>
    <span className="text-xs font-semibold text-slate-600 w-8 text-right">{pct}%</span>
  </div>
);

const OverviewDashboard = ({ onNavigate }) => {
  const toast = useToast();
  const [stats, setStats] = useState({ activePrograms: 0, pendingRequests: 0, expiringCerts: 0, avgCapabilityScore: 0 });
  const [partnerStatus, setPartnerStatus] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, ps] = await Promise.all([
        enablementOverviewService?.getStats(),
        enablementOverviewService?.getPartnerEnablementStatus()
      ]);
      setStats(s);
      setPartnerStatus(ps || []);
    } catch (err) {
      toast?.error?.('Failed to load overview data');
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { label: 'Assign Training', icon: 'BookOpen', section: 'ecity-to-partner', color: 'bg-blue-500 hover:bg-blue-600' },
    { label: 'Log Assessment', icon: 'BarChart3', section: 'assessment', color: 'bg-violet-500 hover:bg-violet-600' },
    { label: 'New Request', icon: 'MessageSquarePlus', section: 'requests', color: 'bg-amber-500 hover:bg-amber-600' },
    { label: 'Issue Certificate', icon: 'Award', section: 'wallet', color: 'bg-emerald-500 hover:bg-emerald-600' },
  ];

  const recentActivity = [
    { icon: 'BookOpen', text: 'Cloud Architecture training assigned to TechPartner', time: '2h ago', color: 'text-blue-500' },
    { icon: 'Award', text: 'AWS cert issued to Sarah Mitchell (eCity)', time: '5h ago', color: 'text-emerald-500' },
    { icon: 'CheckCircle', text: 'Training request accepted by DataSystems Inc', time: '1d ago', color: 'text-violet-500' },
    { icon: 'BarChart3', text: 'Capability assessment updated for Partner A', time: '2d ago', color: 'text-amber-500' },
    { icon: 'GraduationCap', text: 'Partner → eCity program completed: 5 members', time: '3d ago', color: 'text-cyan-500' },
  ];

  return (
    <div className="space-y-16">
      {/* Welcome Banner — reduced height, inline actions */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl px-24 py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-400 rounded-full blur-3xl translate-x-16 -translate-y-16" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-400 rounded-full blur-3xl -translate-x-8 translate-y-8" />
        </div>
        <div className="relative flex items-center justify-between gap-16">
          <div>
            <h2 className="text-xl font-bold text-white mb-2">Enablement Center Overview</h2>
            <p className="text-slate-400 text-xs max-w-lg">
              Track partner readiness, training exchange, certifications, and capability outcomes across your entire partner ecosystem.
            </p>
          </div>
          <div className="flex items-center gap-10 flex-shrink-0">
            <button
              onClick={() => onNavigate?.('outcomes')}
              className="flex items-center gap-8 px-16 py-9 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold transition-colors shadow-lg whitespace-nowrap"
            >
              <Icon name="TrendingUp" size={14} />View Outcomes
            </button>
            <button
              onClick={() => onNavigate?.('reports')}
              className="flex items-center gap-8 px-16 py-9 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors border border-white/20 whitespace-nowrap"
            >
              <Icon name="FileBarChart" size={14} />Reports
            </button>
          </div>
        </div>
      </div>

      {/* Stats Row + Quick Actions as 5th card */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-12">
          {[1,2,3,4,5]?.map(i => (
            <div key={i} className="bg-white rounded-xl border border-slate-200/60 shadow-sm p-14 animate-pulse">
              <div className="h-3 bg-slate-200 rounded w-20 mb-8" />
              <div className="h-7 bg-slate-200 rounded w-12" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-12">
          <StatCard icon="BookOpen" label="Active Programs" value={stats?.activePrograms} sub="Partner-led training" color="bg-blue-500" trend={12} />
          <StatCard icon="MessageSquarePlus" label="Pending Requests" value={stats?.pendingRequests} sub="Awaiting response" color="bg-amber-500" />
          <StatCard icon="AlertTriangle" label="Expiring Certs" value={stats?.expiringCerts} sub="Within 30 days" color="bg-red-500" />
          <StatCard icon="Sparkles" label="Avg Capability" value={`${stats?.avgCapabilityScore}/100`} sub="Across all partners" color="bg-violet-500" trend={8} />
          {/* Quick Actions as 5th card */}
          <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm p-14">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-10 flex items-center gap-6">
              <Icon name="Zap" size={11} className="text-amber-500" />Quick Actions
            </p>
            <div className="grid grid-cols-2 gap-6">
              {quickActions?.map(action => (
                <button
                  key={action?.label}
                  onClick={() => onNavigate?.(action?.section)}
                  className={`flex flex-col items-center gap-4 p-8 rounded-lg text-white text-[10px] font-semibold transition-all hover:scale-105 active:scale-95 ${action?.color}`}
                >
                  <Icon name={action?.icon} size={14} />
                  <span className="leading-tight text-center">{action?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Full-width 3-column grid: Table (2 cols) + Activity (1 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
        {/* Partner Enablement Status Table — 2 cols */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-20 py-14 border-b border-slate-100">
            <div className="flex items-center gap-10">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Icon name="Users" size={14} className="text-white" />
              </div>
              <h3 className="text-sm font-bold text-slate-900">Partner Enablement Status</h3>
            </div>
            <button
              onClick={() => onNavigate?.('outcomes')}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-4"
            >
              View All <Icon name="ArrowRight" size={12} />
            </button>
          </div>
          {loading ? (
            <div className="p-20 space-y-10">
              {[1,2,3]?.map(i => <div key={i} className="h-10 bg-slate-100 rounded-xl animate-pulse" />)}
            </div>
          ) : partnerStatus?.length === 0 ? (
            <div className="p-28 text-center">
              <Icon name="Users" size={28} className="text-slate-300 mx-auto mb-10" />
              <p className="text-slate-500 text-sm">No partner data available</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-16 py-10">Partner</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-10 py-10">Tier</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-10 py-10">Score</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-10 py-10">Completion</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-10 py-10">Certs</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-10 py-10">Last Activity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {partnerStatus?.map(p => (
                    <tr key={p?.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-16 py-12">
                        <p className="text-sm font-semibold text-slate-900 truncate max-w-[140px]">{p?.name}</p>
                      </td>
                      <td className="px-10 py-12"><TierBadge tier={p?.tier} /></td>
                      <td className="px-10 py-12"><ScorePill score={p?.capabilityScore} /></td>
                      <td className="px-10 py-12 min-w-[110px]">
                        <ProgressBar
                          pct={p?.completionPct}
                          color={p?.completionPct >= 80 ? 'bg-emerald-500' : p?.completionPct >= 50 ? 'bg-amber-500' : 'bg-red-400'}
                        />
                      </td>
                      <td className="px-10 py-12">
                        <span className="flex items-center gap-5 text-sm font-semibold text-slate-700">
                          <Icon name="Award" size={13} className="text-amber-500" />
                          {p?.activeCerts}
                        </span>
                      </td>
                      <td className="px-10 py-12">
                        <span className="text-xs text-slate-400">
                          {p?.lastAssessed ? new Date(p?.lastAssessed)?.toLocaleDateString() : 'Not assessed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Activity — 1 col */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-20">
          <h3 className="text-sm font-bold text-slate-900 mb-14 flex items-center gap-8">
            <Icon name="Activity" size={15} className="text-blue-500" />
            Recent Activity
          </h3>
          <div className="space-y-10">
            {recentActivity?.map((item, i) => (
              <div key={i} className="flex items-start gap-10">
                <div className={`w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0 mt-0.5 ${item?.color}`}>
                  <Icon name={item?.icon} size={12} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-700 leading-relaxed">{item?.text}</p>
                  <p className="text-xs text-slate-400 mt-2">{item?.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewDashboard;
